import {
  Button,
  Card,
  Chips,
  Flag,
  Label,
  showNotification,
  Table,
  TableRow,
  Tooltip,
  type ColorSchemeType,
} from 'platform/components';
import {HStack, Link, Show, Text, VStack} from 'platform/foundation';
import {useDateTimeFormatter} from 'platform/locale';
import {match} from 'ts-pattern';

import {useState} from 'react';

import {collectBy, countBy, head, isEmpty, isNil, reject, uniq} from 'ramda';
import {isNilOrEmpty, isNotNilOrEmpty} from 'ramda-adjunct';

import i18n from '@dms/i18n';
import {businessCaseRoutes, testIds} from '@dms/routes';
import {EMPTY_PLACEHOLDER, getCommunicationSectionTitle} from '@dms/shared';

import {composePath, parseDate} from 'shared/web';

import {
  FINANCING_ENDING_OPERATION_ITEM_RESULT,
  type FinancingEndingOperationItemResult,
} from '../../constants/FINANCING_ENDING_OPERATION_ITEM_RESULT';
import {getFinancingEndingOperationItemResultLabel} from '../../utils/getFinancingEndingOperationItemResultLabel';
import type {FinancingEndingOperationItemRow} from '../../utils/getFinancingEndingOperationItemRows';

export type FinancingEndingOperationItemTableProps = {
  rows: FinancingEndingOperationItemRow[];
};

const ALL_RESULTS_FILTER = 'ALL';
const RESULT_ORDER: FinancingEndingOperationItemResult[] = Object.values(
  FINANCING_ENDING_OPERATION_ITEM_RESULT
);
// A group with more rows than this starts collapsed, unless it is the only group.
const EXPANDED_GROUP_MAX_ROWS = 10;
const ADDRESS_SEPARATOR = '\n';
const LONE_GROUP_KEY = 'lone';
const GROUPED_KEY = 'grouped';

const getResultColorScheme = (result: FinancingEndingOperationItemResult) =>
  match<FinancingEndingOperationItemResult, ColorSchemeType>(result)
    .with(FINANCING_ENDING_OPERATION_ITEM_RESULT.SENT, () => 'green')
    .with(
      FINANCING_ENDING_OPERATION_ITEM_RESULT.SKIPPED,
      FINANCING_ENDING_OPERATION_ITEM_RESULT.BLOCKED,
      () => 'orange'
    )
    .with(FINANCING_ENDING_OPERATION_ITEM_RESULT.PENDING, () => 'neutral')
    .otherwise(() => 'red');

const getGroupKey = (row: FinancingEndingOperationItemRow) =>
  `${row.result ?? ''}|${row.reason?.label ?? ''}`;

/**
 * The rows come sorted, problems first. They are grouped by result and reason, so the reason and
 * its hint show once in the group header and the rows have no reason column. The result filter
 * shows only the groups of one result, and "Copy addresses" copies the distinct recipient addresses
 * of the rows it shows. A large group starts collapsed only next to other groups; a card remounts
 * when it becomes or stops being the only group, as it reads its default state on mount only. A row with a sent e-mail links to the message history of its business
 * case. The columns have fixed widths, so filtering does not move them.
 */
export function FinancingEndingOperationItemTable(props: FinancingEndingOperationItemTableProps) {
  const formatDateTime = useDateTimeFormatter();
  const [resultFilter, setResultFilter] = useState<string>(ALL_RESULTS_FILTER);

  const resultCounts = countBy(
    (result) => result,
    props.rows.flatMap((row) => row.result ?? [])
  );
  const presentResults = RESULT_ORDER.filter((result) => (resultCounts[result] ?? 0) > 0);
  const activeFilter =
    resultFilter === ALL_RESULTS_FILTER || presentResults.some((result) => result === resultFilter)
      ? resultFilter
      : ALL_RESULTS_FILTER;
  const visibleRows =
    activeFilter === ALL_RESULTS_FILTER
      ? props.rows
      : props.rows.filter((row) => row.result === activeFilter);
  const groups = collectBy(getGroupKey, visibleRows);
  const visibleAddresses = uniq(
    reject(
      isEmpty,
      visibleRows.flatMap(({item}) => item.recipientEmail ?? [])
    )
  );

  const handleCopyAddresses = () =>
    Promise.resolve()
      .then(() => navigator.clipboard.writeText(visibleAddresses.join(ADDRESS_SEPARATOR)))
      .then(() =>
        showNotification.info(
          i18n.t('entity.businessCase.financingEnding.operation.addressesCopied', {
            count: visibleAddresses.length,
          })
        )
      )
      .catch(() => showNotification.error(i18n.t('general.notifications.copyToClipboardFailed')));

  return (
    <VStack spacing={3}>
      <HStack justify="space-between" align="center" spacing={3}>
        <Chips
          size="small"
          value={[activeFilter]}
          onChange={(values) => setResultFilter(head(values ?? []) ?? ALL_RESULTS_FILTER)}
          options={[
            {
              label: i18n.t('general.labels.all'),
              value: ALL_RESULTS_FILTER,
              suffix: props.rows.length.toString(),
            },
            ...presentResults.map((result) => ({
              label: getFinancingEndingOperationItemResultLabel(result),
              value: result,
              suffix: (resultCounts[result] ?? 0).toString(),
            })),
          ]}
          data-testid={testIds.businessCase.financingEnding('operationResultFilter')}
        />
        <Button
          variant="link"
          size="small"
          leftIcon="content/copy"
          title={i18n.t('entity.businessCase.financingEnding.operation.copyAddresses')}
          isDisabled={visibleAddresses.length === 0}
          onClick={handleCopyAddresses}
          data-testid={testIds.businessCase.financingEnding('operationCopyAddresses')}
        />
      </HStack>
      {groups.map((groupRows) => {
        const [firstRow] = groupRows;
        const groupResult = firstRow.result;
        const groupReason = firstRow.reason;
        const isLoneGroup = groups.length === 1;

        return (
          <Card
            key={`${getGroupKey(firstRow)}|${isLoneGroup ? LONE_GROUP_KEY : GROUPED_KEY}`}
            variant="inlineGrey"
            isExpandable
            isClosedByDefault={!isLoneGroup && groupRows.length > EXPANDED_GROUP_MAX_ROWS}
            title={
              isNil(groupResult) ? null : getFinancingEndingOperationItemResultLabel(groupResult)
            }
            subtitle={groupReason?.label}
            parameters={[
              i18n.t('entity.businessCase.financingEnding.operation.summary.rowCount', {
                count: groupRows.length,
              }),
              groupReason?.hint,
            ]}
            data-testid={testIds.businessCase.financingEnding('operationItemGroup')}
          >
            <Table
              tableLayout="fixed"
              variant="stripped"
              columns={[
                {
                  element: <Label>{i18n.t('page.businessCase.labels.businessCase')}</Label>,
                  width: '25%',
                },
                {element: <Label>{i18n.t('entity.email.labels.recipient')}</Label>},
                {
                  element: (
                    <Label>
                      {i18n.t('entity.businessCase.financingEnding.operation.column.result')}
                    </Label>
                  ),
                  width: '20%',
                },
              ]}
              data-testid={testIds.businessCase.financingEnding('operationItems')}
            >
              {groupRows.map(({item, result}) => (
                <TableRow
                  key={item.recordId}
                  data-testid={testIds.businessCase.financingEnding('operationItem')}
                >
                  <Link
                    size="small"
                    target="_blank"
                    href={composePath(businessCaseRoutes.overview, {
                      params: {id: item.recordId},
                      isSearchParamsPreserved: false,
                    })}
                    data-testid={testIds.businessCase.financingEnding('operationItemLink')}
                  >
                    {item.recordLabel ?? item.recordId}
                  </Link>
                  <VStack
                    data-testid={testIds.businessCase.financingEnding('operationItemRecipient')}
                  >
                    <Show
                      when={isNilOrEmpty(item.recipientName) && isNilOrEmpty(item.recipientEmail)}
                    >
                      <Text size="small">{EMPTY_PLACEHOLDER}</Text>
                    </Show>
                    <Show when={isNotNilOrEmpty(item.recipientName) ? item.recipientName : null}>
                      {(recipientName) => (
                        <Text size="small" overflowWrap="anywhere">
                          {recipientName}
                        </Text>
                      )}
                    </Show>
                    <Show when={isNotNilOrEmpty(item.recipientEmail) ? item.recipientEmail : null}>
                      {(recipientEmail) => (
                        <Text size="xSmall" color="secondary" overflowWrap="anywhere">
                          {recipientEmail}
                        </Text>
                      )}
                    </Show>
                  </VStack>
                  <VStack spacing={1} align="flex-start">
                    <Show when={result}>
                      {(result) => (
                        <Tooltip
                          label={
                            isNil(item.processedAt)
                              ? undefined
                              : i18n.t(
                                  'entity.businessCase.financingEnding.operation.column.processedAt'
                                )
                          }
                          description={
                            isNil(item.processedAt)
                              ? undefined
                              : formatDateTime('dateTimeShort', parseDate(item.processedAt))
                          }
                          data-testid={testIds.businessCase.financingEnding(
                            'operationItemProcessedAt'
                          )}
                        >
                          <Flag
                            size="small"
                            isSubtle
                            colorScheme={getResultColorScheme(result)}
                            label={getFinancingEndingOperationItemResultLabel(result)}
                            data-testid={testIds.businessCase.financingEnding(
                              `operationItemResult-${result}`
                            )}
                          />
                        </Tooltip>
                      )}
                    </Show>
                    <Show when={isNotNilOrEmpty(item.sentMessageId)}>
                      <Link
                        size="xSmall"
                        target="_blank"
                        rel="noopener noreferrer"
                        href={composePath(businessCaseRoutes.communication, {
                          params: {id: item.recordId},
                          isSearchParamsPreserved: false,
                        })}
                        data-testid={testIds.businessCase.financingEnding(
                          'operationItemMessageHistory'
                        )}
                      >
                        {getCommunicationSectionTitle()}
                      </Link>
                    </Show>
                  </VStack>
                </TableRow>
              ))}
            </Table>
          </Card>
        );
      })}
    </VStack>
  );
}
